import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// Shared entity-document model (2.2.1 contratos / 2.2.3 comprovativos / 2.2.4 técnica
// / [3.6.5] documentos da conta corrente do cliente / "general" = documentos gerais da
// empresa, sem proprietário).
export type DocumentSection =
  | "contract"
  | "technical"
  | "payment_proof"
  | "client_account"
  // Autos de medição e outros anexos de uma fatura de despesa (dono = financial-entry).
  | "auto"
  // Faturas adicionais da mesma linha de despesa (dono = financial-entry): a 1.ª é o documento
  // principal; as restantes entram por aqui.
  | "invoice"
  | "general";

export type DocumentOwnerType =
  | "work"
  | "client"
  | "supplier"
  | "subcontractor"
  | "financial-entry"
  | "general";

export interface EntityDocumentDto {
  id: number;
  section: DocumentSection;
  ownerType: DocumentOwnerType;
  ownerId: number;
  workId: number | null;
  clientId: number | null;
  supplierId: number | null;
  subcontractorId: number | null;
  workFinancialEntryId: number | null;
  title: string;
  category: string | null;
  counterpartyName: string | null;
  reference: string | null;
  documentDate: string | null;
  notes: string | null;
  fileName: string;
  contentType: string;
  sizeInBytes: number;
  created: string;
}

// Fields the upload form contributes (besides the File itself).
export interface UploadEntityDocumentInput {
  file: File;
  ownerType: DocumentOwnerType;
  ownerId: number;
  section: DocumentSection;
  title: string;
  category?: string | null;
  counterpartyName?: string | null;
  reference?: string | null;
  documentDate?: string | null;
  notes?: string | null;
}

// Editable metadata (everything except the file).
export interface UpdateEntityDocumentPayload {
  title: string;
  category?: string | null;
  counterpartyName?: string | null;
  reference?: string | null;
  documentDate?: string | null;
  notes?: string | null;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export interface GetEntityDocumentsOptions {
  section?: DocumentSection;
  search?: string;
}

export function getEntityDocuments(
  ownerType: DocumentOwnerType,
  ownerId: number,
  opts?: GetEntityDocumentsOptions,
): Promise<EntityDocumentDto[]> {
  const params = new URLSearchParams();
  params.set("ownerType", ownerType);
  params.set("ownerId", String(ownerId));
  if (opts?.section) params.set("section", opts.section);
  if (opts?.search && opts.search.trim() !== "") {
    params.set("search", opts.search.trim());
  }
  return request<EntityDocumentDto[]>(`/api/documents?${params.toString()}`);
}

export function uploadEntityDocument(
  input: UploadEntityDocumentInput,
): Promise<EntityDocumentDto> {
  const data = new FormData();
  data.append("file", input.file);
  data.append("ownerType", input.ownerType);
  data.append("ownerId", String(input.ownerId));
  data.append("section", input.section);
  data.append("title", input.title);
  if (input.category) data.append("category", input.category);
  if (input.counterpartyName) data.append("counterpartyName", input.counterpartyName);
  if (input.reference) data.append("reference", input.reference);
  if (input.documentDate) data.append("documentDate", input.documentDate);
  if (input.notes) data.append("notes", input.notes);

  // No Content-Type header — the browser sets the multipart boundary.
  return request<EntityDocumentDto>("/api/documents", {
    method: "POST",
    body: data,
  });
}

export function updateEntityDocument(
  id: number,
  payload: UpdateEntityDocumentPayload,
): Promise<EntityDocumentDto> {
  return request<EntityDocumentDto>(`/api/documents/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: payload.title,
      category: payload.category ?? null,
      counterpartyName: payload.counterpartyName ?? null,
      reference: payload.reference ?? null,
      documentDate: payload.documentDate ?? null,
      notes: payload.notes ?? null,
    }),
  });
}

export async function deleteEntityDocument(id: number): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/documents/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}

/** O ficheiro em si, para o viewer o mostrar dentro da app. */
export async function fetchEntityDocumentFile(id: number): Promise<Blob> {
  const response = await fetch(getEntityDocumentFileUrl(id), {
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return response.blob();
}

// URL for opening the raw file inline in a new tab (<a target="_blank">).
export function getEntityDocumentFileUrl(id: number): string {
  return `${apiBaseUrl}/api/documents/${id}/file`;
}
